import clsx from 'clsx';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Card, DashboardContext, ReactTable, SanitizedHtml, Skeleton, StrapiImg } from 'components';
import { formatDate } from 'helpers';
import { useFetch, useUser } from 'hooks';

const DashboardSponsorship = () => {
  const { t } = useTranslation();
  const { currentUser } = useUser();
  const { getAccountPage, accountData, accountLoading } = useContext(DashboardContext);
  const { call: sponsorshipCall, loading: sponsorshipLoading, data: sponsorship } = useFetch();

  const benefits = useMemo(() => accountData?.data?.[0]?.benefits, [accountData]);
  const godchildrenColumns = useMemo(
    () => [
      {
        accessorKey: 'name',
        enableSorting: false,
        header: t('dashboard:table.header.lastname'),
        cell: (cell) => <span>{`${cell.row.original.name} ${cell.row.original.firstname}`}</span>
      },
      {
        accessorKey: 'sponsorshipDate',
        enableSorting: false,
        header: t('dashboard:table.header.sponsorshipDate'),
        cell: (cell) => <span>{formatDate(cell.row.original.sponsorshipDate)}</span>
      }
    ],
    []
  );

  useEffect(() => {
    getAccountPage('sponsorship');
    getSponsorship();
  }, []);

  const getSponsorship = () => {
    if (!currentUser?.serialNumber) return;

    sponsorshipCall({
      service: 'castelis',
      path: `/users/sponsorships/${currentUser.serialNumber}`,
      method: 'get'
    });
  };

  return (
    <div className="flex flex-col lg:grid lg:grid-cols-12 lg:gap-4">
      <div className={clsx(accountLoading || accountData?.data?.length > 0 ? 'lg:col-span-8' : 'hidden')}>
        {accountLoading && <Skeleton classNames="h-96 w-full rounded-xl" />}
        {!accountLoading && accountData?.data?.length > 0 && (
          <Card onMobile={false}>
            {accountData.data[0]?.title && <h2 className="h2 underlined-yellow mb-8"> {accountData.data[0]?.title}</h2>}
            <SanitizedHtml content={accountData.data[0]?.intro} />

            {benefits?.map((benefit, index) => (
              <div className="flex flex-row mt-8 lg:gap-4 " key={`benefit-${index}`}>
                <div className="rounded-lg hidden shrink-0 overflow-hidden lg:w-56 lg:h-40 lg:block">
                  <StrapiImg image={benefit?.thumbnail} className="w-full h-full object-cover" />
                </div>

                <div className="flex flex-col justify-between gap-2">
                  <div>
                    {benefit?.title && (
                      <p className="h3 underlined-yellow mb-8">
                        <span className="truncate-line one-line">{benefit.title}</span>
                      </p>
                    )}
                    <SanitizedHtml content={benefit?.content} />
                  </div>

                  {benefit?.cta?.buttonText && (
                    <a href={benefit?.cta?.link} className="btn w-fit small text-sm lg:text-base">
                      {benefit?.cta?.buttonText}
                    </a>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>

      <div className="max-lg:mt-8 lg:col-span-4">
        <div className="flex flex-col gap-12 lg:gap-8">
          {sponsorshipLoading && (
            <>
              <Skeleton classNames="h-64 w-full rounded-xl" />
              <Skeleton classNames="h-64 w-full rounded-xl" />
            </>
          )}

          {!sponsorshipLoading && (
            <Card onMobile={false}>
              <h2 className="h2 underlined-yellow mb-8">{t('dashboard:sponsorship.invitation')}</h2>
              <div className="flex flex-wrap gap-2 items-center">
                <p className="grow font-semibold text-gray-700 uppercase">{t('dashboard:sponsorship.code')}</p>

                <div className="bg-gray-200 rounded-md p-3 text-gray-600 shrink-0">
                  {sponsorship?.sponsorships?.sponsorKey ?? 'N/A'}
                </div>
              </div>
            </Card>
          )}

          {!sponsorshipLoading && sponsorship?.sponsorships?.godchildrenCount > 0 && (
            <Card onMobile={false}>
              <h2 className="h2 underlined-yellow mb-8">{t('dashboard:sponsorship.godchildren')}</h2>
              <ReactTable columns={godchildrenColumns} data={sponsorship.sponsorships.godchildrenList} />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardSponsorship;
